import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SubjectOfferingService, SubjectService, ProfileService,
  FormService, AcademicYearService } from '../../../services';


@Component({
  selector: 'app-subject-offerings',
  standalone: false,
  templateUrl: './subject-offerings.html',
  styleUrl: './subject-offerings.css',
})
export class SubjectOfferings implements OnInit {

  loading = false;
  errorMsg = '';
  allOfferings: any[] = [];
  filteredOfferings: any[] = [];
  pagedOfferings: any[] = [];

  allSubjects: any[] = [];
  allTeachers: any[] = [];
  allForms: any[] = [];
  allAcademicYears: any[] = [];

  searchQuery = '';
  page = 0;
  size = 10;

  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showDetailModal = false;
  showAssignModal = false;
  showTopicAddModal = false;
  showTopicEditModal = false;
  showTopicDeleteModal = false;

  selectedOffering: any = null;
  selectedTopic: any = null;

  topics: any[] = [];
  topicsLoading = false;
  topicsError = '';

  addForm: any = { subjectId: '', formId: '', academicYearId: '', assignedTeacherId: '' };
  editForm: any = { id: null, subjectId: '', formId: '', academicYearId: '', assignedTeacherId: '' };
  assignForm: any = { assignedTeacherId: '' };
  topicAddForm: any = { title: '', description: '' };
  topicEditForm: any = { id: null, title: '', description: '' };

  addLoading = false;
  editLoading = false;
  deleteLoading = false;
  assignLoading = false;
  topicAddLoading = false;
  topicEditLoading = false;
  topicDeleteLoading = false;

  addError = '';
  editError = '';
  deleteError = '';
  assignError = '';
  topicAddError = '';
  topicEditError = '';
  topicDeleteError = '';

  constructor(
    private offeringService: SubjectOfferingService,
    private subjectService: SubjectService,
    private profileService: ProfileService,
    private formService: FormService,
    private academicYearService: AcademicYearService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchOfferings();
    this.fetchSubjects();
    this.fetchTeachers();
    this.fetchForms();
    this.fetchAcademicYears();
  }

  fetchOfferings() {
    this.loading = true;
    this.errorMsg = '';
    this.cdr.detectChanges();
    this.offeringService.getAll(0, 1000).subscribe({
      next: (res: any) => {
        this.allOfferings = res?.data?.content ?? res?.data ?? [];
        this.applyFilter();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.errorMsg = err?.error?.message || 'Failed to load offerings';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  fetchSubjects() {
    this.subjectService.getSubjects().subscribe({
      next: (res: any) => {
        this.allSubjects = res?.data ?? [];
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  fetchTeachers() {
    this.profileService.getStaff(0, 200).subscribe({
      next: (res: any) => {
        const staff = res?.data?.content ?? res?.data ?? [];
        this.allTeachers = staff.filter((s: any) =>
          s.role?.toLowerCase().includes('teacher') ||
          s.staffPosition?.toLowerCase().includes('teacher')
        );
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  fetchForms() {
    this.formService.getForms().subscribe({
      next: (res: any) => {
        this.allForms = res?.data ?? [];
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  fetchAcademicYears() {
    this.academicYearService.getAcademicYears().subscribe({
      next: (res: any) => {
        this.allAcademicYears = res?.data ?? [];
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredOfferings = q
      ? this.allOfferings.filter(o =>
          o.subjectName?.toLowerCase().includes(q) ||
          o.grade?.toLowerCase().includes(q) ||
          o.academicYear?.toLowerCase().includes(q)
        )
      : [...this.allOfferings];
    this.page = 0;
    this.updatePage();
  }

  updatePage() {
    const start = this.page * this.size;
    this.pagedOfferings = this.filteredOfferings.slice(start, start + this.size);
  }

  get totalPages(): number { return Math.ceil(this.filteredOfferings.length / this.size); }
  get hasNext(): boolean { return this.page < this.totalPages - 1; }
  get hasPrev(): boolean { return this.page > 0; }

  nextPage() { if (!this.hasNext) return; this.page++; this.updatePage(); }
  prevPage() { if (!this.hasPrev) return; this.page--; this.updatePage(); }
  onSearch() { this.applyFilter(); }
  getMinValue(a: number, b: number) { return Math.min(a, b); }

  getSubjectById(id: any) {
    return this.allSubjects.find(s => s.id == id);
  }

  getTeacherById(id: any) {
    return this.allTeachers.find(t => t.id == id);
  }

  openAddModal() {
    this.addForm = { subjectId: '', formId: '', academicYearId: '', assignedTeacherId: '' };
    this.addError = '';
    this.showAddModal = true;
  }

  closeAddModal() {
    if (this.addLoading) return;
    this.showAddModal = false;
    this.addError = '';
  }

  submitAdd() {
    if (!this.addForm.subjectId || !this.addForm.formId || !this.addForm.academicYearId) {
      this.addError = 'Subject, grade, and academic year are required.';
      return;
    }
    this.addLoading = true;
    this.addError = '';
    const payload: any = {
      subjectId: +this.addForm.subjectId,
      formId: this.addForm.formId,
      academicYearId: this.addForm.academicYearId,
    };
    if (this.addForm.assignedTeacherId) {
      payload.assignedTeacherId = +this.addForm.assignedTeacherId;
    }
    this.offeringService.createOffering(payload).subscribe({
      next: () => {
        this.addLoading = false;
        this.showAddModal = false;
        this.fetchOfferings();
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.addLoading = false;
        this.addError = err?.error?.message || 'Failed to create offering.';
        this.cdr.detectChanges();
      }
    });
  }

  openEditModal(offering: any) {
    this.selectedOffering = offering;
    this.editForm = {
      id: offering.id,
      subjectId: offering.subjectId ?? '',
      formId: offering.formId ?? '',
      academicYearId: offering.academicYearId ?? '',
      assignedTeacherId: offering.assignedTeacherId ?? ''
    };
    this.editError = '';
    this.showEditModal = true;
  }

  closeEditModal() {
    if (this.editLoading) return;
    this.showEditModal = false;
    this.editError = '';
    this.selectedOffering = null;
  }

  submitEdit() {
    if (!this.editForm.subjectId || !this.editForm.formId || !this.editForm.academicYearId) {
      this.editError = 'Subject, form, and academic year are required.';
      return;
    }
    this.editLoading = true;
    this.editError = '';
    const payload: any = {
      subjectOfferingId: this.editForm.id,
      subjectId: +this.editForm.subjectId,
      formId: this.editForm.formId,
      academicYearId: this.editForm.academicYearId,
    };
    if (this.editForm.assignedTeacherId) {
      payload.assignedTeacherId = +this.editForm.assignedTeacherId;
    }
    this.offeringService.updateOffering(payload).subscribe({
      next: () => {
        this.editLoading = false;
        this.showEditModal = false;
        this.fetchOfferings();
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.editLoading = false;
        this.editError = err?.error?.message || 'Failed to update offering.';
        this.cdr.detectChanges();
      }
    });
  }

  openDeleteModal(offering: any) {
    this.selectedOffering = offering;
    this.deleteError = '';
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    if (this.deleteLoading) return;
    this.showDeleteModal = false;
    this.deleteError = '';
    this.selectedOffering = null;
  }

  confirmDelete() {
    if (!this.selectedOffering) return;
    this.deleteLoading = true;
    this.deleteError = '';
    this.offeringService.deleteOffering(this.selectedOffering.id).subscribe({
      next: () => {
        this.deleteLoading = false;
        this.showDeleteModal = false;
        this.fetchOfferings();
      },
      error: (err: any) => {
        this.deleteLoading = false;
        this.deleteError = err?.error?.message || 'Failed to delete offering.';
        this.cdr.detectChanges();
      }
    });
  }

  openAssignModal(offering: any) {
    this.selectedOffering = offering;
    this.assignForm = { assignedTeacherId: offering.assignedTeacherId ?? '' };
    this.assignError = '';
    this.showAssignModal = true;
  }

  closeAssignModal() {
    if (this.assignLoading) return;
    this.showAssignModal = false;
    this.assignError = '';
    this.selectedOffering = null;
  }

  submitAssign() {
    if (!this.assignForm.assignedTeacherId) {
      this.assignError = 'Please select a teacher.';
      return;
    }
    this.assignLoading = true;
    this.assignError = '';
    this.offeringService.assignTeacher(this.selectedOffering.id, +this.assignForm.assignedTeacherId).subscribe({
      next: () => {
        this.assignLoading = false;
        this.showAssignModal = false;
        this.fetchOfferings();
      },
      error: (err: any) => {
        this.assignLoading = false;
        this.assignError = err?.error?.message || 'Failed to assign teacher.';
        this.cdr.detectChanges();
      }
    });
  }

  openDetailModal(offering: any) {
    this.selectedOffering = offering;
    this.showDetailModal = true;
    this.fetchTopics(offering.id);
  }

  closeDetailModal() {
    this.showDetailModal = false;
    this.selectedOffering = null;
    this.topics = [];
    this.topicsError = '';
  }

  fetchTopics(offeringId: number) {
    this.topicsLoading = true;
    this.topicsError = '';
    this.cdr.detectChanges();
    this.offeringService.getTopics(offeringId).subscribe({
      next: (res: any) => {
        this.topics = res?.data ?? [];
        this.topicsLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.topicsError = err?.error?.message || 'Failed to load topics.';
        this.topicsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  openTopicAddModal() {
    this.topicAddForm = { title: '', description: '' };
    this.topicAddError = '';
    this.showTopicAddModal = true;
  }

  closeTopicAddModal() {
    if (this.topicAddLoading) return;
    this.showTopicAddModal = false;
    this.topicAddError = '';
  }

  submitTopicAdd() {
    if (!this.topicAddForm.title.trim()) {
      this.topicAddError = 'Title is required.';
      return;
    }
    this.topicAddLoading = true;
    this.topicAddError = '';
    this.offeringService.createTopic(this.selectedOffering.id, this.topicAddForm).subscribe({
      next: () => {
        this.topicAddLoading = false;
        this.showTopicAddModal = false;
        this.fetchTopics(this.selectedOffering.id);
      },
      error: (err: any) => {
        this.topicAddLoading = false;
        this.topicAddError = err?.error?.message || 'Failed to add topic.';
        this.cdr.detectChanges();
      }
    });
  }

  openTopicEditModal(topic: any) {
    this.selectedTopic = topic;
    this.topicEditForm = { id: topic.id, title: topic.title, description: topic.description ?? '' };
    this.topicEditError = '';
    this.showTopicEditModal = true;
  }

  closeTopicEditModal() {
    if (this.topicEditLoading) return;
    this.showTopicEditModal = false;
    this.topicEditError = '';
    this.selectedTopic = null;
  }

  submitTopicEdit() {
    if (!this.topicEditForm.title.trim()) {
      this.topicEditError = 'Title is required.';
      return;
    }
    this.topicEditLoading = true;
    this.topicEditError = '';
    this.offeringService.updateTopic(this.selectedOffering.id, this.topicEditForm).subscribe({
      next: () => {
        this.topicEditLoading = false;
        this.showTopicEditModal = false;
        this.fetchTopics(this.selectedOffering.id);
      },
      error: (err: any) => {
        this.topicEditLoading = false;
        this.topicEditError = err?.error?.message || 'Failed to update topic.';
        this.cdr.detectChanges();
      }
    });
  }

  openTopicDeleteModal(topic: any) {
    this.selectedTopic = topic;
    this.topicDeleteError = '';
    this.showTopicDeleteModal = true;
  }

  closeTopicDeleteModal() {
    if (this.topicDeleteLoading) return;
    this.showTopicDeleteModal = false;
    this.topicDeleteError = '';
    this.selectedTopic = null;
  }

  confirmTopicDelete() {
    if (!this.selectedTopic) return;
    this.topicDeleteLoading = true;
    this.topicDeleteError = '';
    this.offeringService.deleteTopic(this.selectedOffering.id, this.selectedTopic.id).subscribe({
      next: () => {
        this.topicDeleteLoading = false;
        this.showTopicDeleteModal = false;
        this.fetchTopics(this.selectedOffering.id);
      },
      error: (err: any) => {
        this.topicDeleteLoading = false;
        this.topicDeleteError = err?.error?.message || 'Failed to delete topic.';
        this.cdr.detectChanges();
      }
    });
  }
}